/**
 * LocationModal
 * Inserisce coordinate GPS e titolo per inviare una posizione
 */

import { useState } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';

interface LocationModalProps {
  onClose: () => void;
}

export function LocationModal({ onClose }: LocationModalProps) {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const { sendLocationMessage } = useWhatsAppChatStore();

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Questo browser non sa rilevare la posizione: inserisci le coordinate a mano');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setGeoLoading(false);
      },
      () => {
        setGeoLoading(false);
        toast.error('Posizione non disponibile: consenti l\'accesso alla posizione dal lucchetto nella barra degli indirizzi, oppure inserisci le coordinate');
      }
    );
  };

  const handleSend = async () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (isNaN(latNum) || isNaN(lngNum)) return;
    setLoading(true);
    try {
      // Il titolo e' obbligatorio per WAHA: se l'utente non lo scrive si manda "Posizione".
      await sendLocationMessage(latNum, lngNum, title.trim() || 'Posizione');
      onClose();
    } catch (err) {
      console.error('[LocationModal] send error:', err);
      toast.error('Invio della posizione non riuscito. Riprova tra poco.');
      setLoading(false);
    }
  };

  const isValid = !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <MapPin className="w-5 h-5 text-[#787774]" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Invia posizione</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <button
            onClick={handleGetCurrentLocation}
            disabled={geoLoading}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-[#2383E2] border border-[#2383E2]/30 rounded-lg hover:bg-[#2383E2]/5 transition-colors disabled:opacity-50"
          >
            {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            Usa posizione attuale
          </button>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#787774] mb-1 block">Latitudine</label>
              <input
                type="number"
                step="any"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="es. 45.4642"
                className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2]"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-[#787774] mb-1 block">Longitudine</label>
              <input
                type="number"
                step="any"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="es. 9.1900"
                className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#787774] mb-1 block">Titolo (opzionale)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="es. Ufficio"
              className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2]"
            />
          </div>

          <button
            onClick={handleSend}
            disabled={!isValid || loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-lg transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            {loading ? 'Invio...' : 'Invia posizione'}
          </button>
        </div>
      </div>
    </div>
  );
}
